import React from 'react';
import { 
  EnhancedForm, 
  FormField, 
  FormSection, 
  FormActions, 
  useFormValidation, 
  useAsyncFormHandler, 
  validationRules 
} from '../ui/EnhancedForm';
import { ErrorMessage } from '../ui/ErrorStates';
import { ButtonLoading } from '../ui/LoadingStates';
import Input from '../ui/Input';
import Button from '../ui/Button';
import Icon from '../AppIcon';

const EnhancedContactForm = ({ 
  title = "Get in Touch",
  description = "Let's discuss your GCC requirements",
  onSubmit,
  className 
}) => {
  // Form validation setup
  const {
    values,
    errors,
    setValue,
    validateForm,
    resetForm
  } = useFormValidation(
    {
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      position: '',
      phone: '',
      message: '',
      timeline: '',
      budget: ''
    },
    {
      firstName: [validationRules.required('First name is required')],
      lastName: [validationRules.required('Last name is required')],
      email: [
        validationRules.required('Email is required'),
        validationRules.email()
      ],
      company: [validationRules.required('Company name is required')],
      message: [
        validationRules.required('Message is required'),
        validationRules.minLength(10, 'Please provide at least 10 characters')
      ],
      phone: [
        validationRules.pattern(
          /^[\+]?[1-9][\d]{0,15}$/,
          'Please enter a valid phone number'
        )
      ]
    }
  );

  // Async form handler
  const { loading, error, success, handleSubmit: handleAsyncSubmit } = useAsyncFormHandler();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    await handleAsyncSubmit(async () => {
      // Send email via Web3Forms (free email service)
      const formData = {
        access_key: '6d4cf7ff-8ada-4e47-8a17-d90a6759266d',
        subject: `New GCC Inquiry from ${values.firstName} ${values.lastName} - ${values.company}`,
        from_name: `${values.firstName} ${values.lastName}`,
        to: 'sales@bootminds.com',
        // Form fields
        name: `${values.firstName} ${values.lastName}`,
        email: values.email,
        phone: values.phone || 'Not provided',
        company: values.company,
        position: values.position || 'Not provided',
        timeline: values.timeline || 'Not specified',
        budget: values.budget || 'Not specified',
        message: values.message,
        // Auto-response settings
        replyto: values.email,
        // Formatted email body
        'Full Name': `${values.firstName} ${values.lastName}`,
        'Email': values.email,
        'Phone': values.phone || 'Not provided',
        'Company': values.company,
        'Position': values.position || 'Not provided',
        'Timeline': values.timeline || 'Not specified',
        'Budget Range': values.budget || 'Not specified',
        'Message': values.message
      };

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to send message. Please try again.');
      }
      
      if (onSubmit) {
        await onSubmit(values);
      }
      
      resetForm();
    });
  };

  const timelineOptions = [
    { value: '', label: 'Select timeline...' },
    { value: 'immediate', label: 'Immediate (within 30 days)' },
    { value: '3-months', label: '3 months' },
    { value: '6-months', label: '6 months' },
    { value: '12-months', label: '12+ months' }
  ];

  const budgetOptions = [
    { value: '', label: 'Select budget range...' },
    { value: 'under-100k', label: 'Under $100K' },
    { value: '100k-500k', label: '$100K - $500K' },
    { value: '500k-1m', label: '$500K - $1M' },
    { value: 'over-1m', label: 'Over $1M' },
    { value: 'not-disclosed', label: 'Prefer not to disclose' }
  ];

  if (success) {
    return (
      <div className="text-center space-y-6 p-8 bg-success/5 border border-success/20 rounded-xl">
        <div className="mx-auto w-16 h-16 bg-success/10 rounded-full flex items-center justify-center">
          <Icon name="CheckCircle" size={32} className="text-success" />
        </div>
        
        <div className="space-y-2">
          <h3 className="text-heading-lg font-semibold text-success">
            Message Sent Successfully!
          </h3>
          <p className="text-body-md text-secondary">
            Thank you for your enquiry. Someone from our team will reach out to you soon to discuss your GCC requirements.
          </p>
        </div>
        
        <Button 
          onClick={() => window.location.reload()}
          variant="outline"
          size="lg"
          iconName="RefreshCw"
          iconPosition="left"
        >
          Send Another Message
        </Button>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="mb-8 text-center">
        <h2 className="text-heading-xl font-bold text-text-primary mb-4">
          {title}
        </h2>
        <p className="text-body-lg text-secondary">
          {description}
        </p>
      </div>

      <EnhancedForm
        onSubmit={handleSubmit}
        loading={loading}
        loadingMessage="Sending your message..."
        errors={errors}
        showErrorSummary={true}
        className="max-w-2xl mx-auto"
      >
        {/* Global form error */}
        {error && (
          <ErrorMessage 
            message={error}
            type="network"
            onRetry={() => handleSubmit()}
          />
        )}

        {/* Personal Information Section */}
        <FormSection
          title="Personal Information"
          description="Tell us about yourself"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="First Name"
              htmlFor="firstName"
              error={errors.firstName}
              required
            >
              <Input
                id="firstName"
                type="text"
                placeholder="John"
                value={values.firstName}
                onChange={(e) => setValue('firstName', e.target.value)}
                error={!!errors.firstName}
              />
            </FormField>

            <FormField
              label="Last Name"
              htmlFor="lastName"
              error={errors.lastName}
              required
            >
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                value={values.lastName}
                onChange={(e) => setValue('lastName', e.target.value)}
                error={!!errors.lastName}
              />
            </FormField>
          </div>

          <FormField
            label="Email Address"
            htmlFor="email"
            error={errors.email}
            required
            description="We'll use this to send you updates and proposals"
          >
            <Input
              id="email"
              type="email"
              placeholder="john.doe@company.com"
              value={values.email}
              onChange={(e) => setValue('email', e.target.value)}
              error={!!errors.email}
              iconName="Mail"
              iconPosition="left"
            />
          </FormField>

          <FormField
            label="Phone Number"
            htmlFor="phone"
            error={errors.phone}
            description="Optional - for urgent matters"
          >
            <Input
              id="phone"
              type="tel"
              placeholder="+1 (555) 123-4567"
              value={values.phone}
              onChange={(e) => setValue('phone', e.target.value)}
              error={!!errors.phone}
              iconName="Phone"
              iconPosition="left"
            />
          </FormField>
        </FormSection>

        {/* Company Information Section */}
        <FormSection
          title="Company Information"
          description="Help us understand your organization"
        >
          <FormField
            label="Company Name"
            htmlFor="company"
            error={errors.company}
            required
          >
            <Input
              id="company"
              type="text"
              placeholder="Acme Corporation"
              value={values.company}
              onChange={(e) => setValue('company', e.target.value)}
              error={!!errors.company}
              iconName="Building"
              iconPosition="left"
            />
          </FormField>

          <FormField
            label="Your Position"
            htmlFor="position"
            error={errors.position}
            description="Your role in the organization"
          >
            <Input
              id="position"
              type="text"
              placeholder="CTO, VP Engineering, etc."
              value={values.position}
              onChange={(e) => setValue('position', e.target.value)}
              error={!!errors.position}
              iconName="User"
              iconPosition="left"
            />
          </FormField>
        </FormSection>

        {/* Project Details Section */}
        <FormSection
          title="Project Details"
          description="Tell us about your GCC requirements"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Timeline"
              htmlFor="timeline"
              error={errors.timeline}
            >
              <select
                id="timeline"
                value={values.timeline}
                onChange={(e) => setValue('timeline', e.target.value)}
                className="w-full h-11 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {timelineOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField
              label="Budget Range"
              htmlFor="budget"
              error={errors.budget}
            >
              <select
                id="budget"
                value={values.budget}
                onChange={(e) => setValue('budget', e.target.value)}
                className="w-full h-11 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {budgetOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>
          </div>

          <FormField
            label="Message"
            htmlFor="message"
            error={errors.message}
            required
            description="Describe your GCC requirements, challenges, or questions"
          >
            <textarea
              id="message"
              rows={6}
              placeholder="Tell us about your Global Capability Center requirements, current challenges, or specific questions you have. The more details you provide, the better we can assist you."
              value={values.message}
              onChange={(e) => setValue('message', e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
            />
          </FormField>
        </FormSection>

        {/* Form Actions */}
        <FormActions align="center">
          <Button
            type="submit"
            size="xl"
            loading={loading}
            disabled={loading}
            className="bg-primary hover:bg-primary/90 text-primary-foreground min-w-[200px]"
            iconName="Send"
            iconPosition="left"
          >
            {loading ? 'Sending Message...' : 'Send Message'}
          </Button>
        </FormActions>

        {/* Loading indicator when processing */}
        {loading && (
          <div className="text-center">
            <ButtonLoading 
              text="Processing your message..."
              size="sm"
            />
          </div>
        )}
      </EnhancedForm>
    </div>
  );
};

export default EnhancedContactForm;
